(ns app.front.view.create-application.create-v2
  (:require [app.domain.address.model :as address-model]
            [app.domain.branding.model :as branding-model]
            [app.domain.business.model :as business-model]
            [app.domain.group.config.model :as config-model]
            [app.domain.group.model :as group-model]
            [app.domain.group.permit-type-config.model :as ptc-model]
            [app.domain.municipality.model :as municipality-model]
            [app.domain.project.model :as project-model]
            [app.domain.property.model :as property-model]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.municipality :as municipality]
            [app.front.ui :as ui]
            [app.front.ui.cards :as cards]
            [app.front.ui.column-select :as columns]
            [app.front.ui.error-boundary :as eb]
            [app.front.ui.radio :as ui-radio]
            [app.front.ui.rich-text :as ui-rt]
            [app.front.ui.select :as ui-select]
            [app.front.ui.tabs :as ui-tabs]
            [app.front.util :as futil :refer [for-frag]]
            [app.front.view.application-wizard.core :as application-wizard]
            [app.front.view.create-application.gis-search :as gis-search]
            [app.front.view.map-common.util :as map-common-util]
            [app.front.view.not-found :as not-found]
            [app.front.view.property.attribute :as property-attribute]
            [app.front.view.wizard.property-selector :as property-selector]
            [app.front.view.workspace.address :as workspace-address]
            [app.front.view.workspace.location :as location]
            [app.front.view.workspace.map :as workspace-map]
            [app.i18n.model :as i18n-model]
            [app.rich-text :as rich-text]
            [cljs.reader]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [core.front.icons :as i]
            [core.front.media-query :as media-query]
            [core.front.routing :as routing]
            [core.user.model :as user-model]
            [core.util.normalize :as normalize]
            [core.util.schemas :as schemas]
            [core.util.string :as str-util]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-chain.core :as rc]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]
            [reagent.ratom :as ra]
            [schema-tools.core :as st]
            [schema.core :as s]))


(def keyword->image
  {:permit-type/B              "cloudpermit_building_icon.svg"
   :permit-type/P              "cloudpermit_planning_icon.svg"
   :permit-type/PW             "cloudpermit_public_works_icon.svg"
   :permit-type/L              "licence.svg"
   :permit-type/CR             "contractor_registration.svg"
   :permit-type/LU             "cloudpermit_planning_icon.svg" ; TODO use proper icon
   :permit-type/CE             "cloudpermit_public_works_icon.svg"
   :permit-type/SW             "solid_waste.svg"
   :project-type/agricultural  "agricultural.svg"
   :project-type/residential   "residential.svg"
   :project-type/commercial    "commercial.svg"
   :project-type/industrial    "industrial.svg"
   :project-type/institutional "institutional.svg"
   :project-type/mixed-use     "mixed_use.svg"
   :project-type/other         "other.svg"})


(s/defschema WizardParams
  (st/merge
    {:province           s/Str
     :project/name       s/Str
     :project/id         s/Int
     :permit-type        s/Keyword
     :category           s/Int
     :work-types         [s/Int]
     :work-target        s/Int
     :zoom               s/Int
     :project-type       s/Keyword
     :applicant-type     s/Str
     :applicant-id       s/Str
     :create-mode        s/Str
     :submit-ts          s/Int
     :authority-group-id s/Int}
    property-selector/LocationWizardParams))


(defn most-specific-targets
  "Given a category, work-types and a work target, give a vector containing the most specific of these that is not nil"
  [category work-types work-target]
  (or (some-> work-target vector)
      (seq work-types)
      (some-> category vector)))


(defn municipality-configs
  "Municipality entries for the given municipality. There can be several of them, one per domain
   group serving the same area (e.g. a lower-tier and an upper-tier authority)."
  [subdivisions+municipalities municipality subdivision-or-country]
  (when (and municipality subdivision-or-country)
    (->> (get subdivisions+municipalities subdivision-or-country)
         (filter #(-> % :municipality (= municipality))))))


(defn pick-municipality-config
  "Picks the entry of `domain-group-id`, or the first entry when that domain group does not serve
   the municipality (only domain groups with a GIS configuration are listed)."
  [configs domain-group-id]
  (or (when domain-group-id
        (p/find-first configs {:domain-group-id domain-group-id}))
      (first configs)))


(defn find-municipality-config
  ([db municipality subdivision-or-country]
   (find-municipality-config db municipality subdivision-or-country nil))
  ([db municipality subdivision-or-country domain-group-id]
   (-> (::municipality/subdivisions+municipalities db)
       (municipality-configs municipality subdivision-or-country)
       (pick-municipality-config domain-group-id))))


(defn permit-type-groups
  "Authority groups handling the selected permit type, as returned by the wizard config."
  [permit-type->groups permit-type]
  (or (get permit-type->groups permit-type)
      (some-> permit-type->groups vals first)))


(defn authority-group-for-category
  "Authority group that handles `category`. The backend sorts the groups by category count, so the
   first one is the best guess as long as no category has been selected."
  [groups category]
  (or (p/find-first groups #(-> % :category-gc-ids (contains? category)))
      (first groups)))


(defn selected-authority-group [db]
  (-> (permit-type-groups (::permit-type->groups db) (::selected-permit-type db))
      (authority-group-for-category (::selected-category db))))


(defn domain-group-id
  "Domain group to be used for GIS operations (map layers, address and point search, property
   owners). Categories of the same permit type can be handled by different authority groups, each
   living under its own domain group with its own GIS and map configuration, so the domain group is
   resolved from the selected permit type + category. Falls back to the municipality default when
   the authority group is not known yet or its domain group does not serve the municipality."
  [db]
  (or (:domain-group-id (find-municipality-config db
                                                  (::selected-municipality db)
                                                  (::selected-subdivision db)
                                                  (:domain-group-id (selected-authority-group db))))
      (::municipality-domain-group-id db)))


(rf/reg-event-fx ::initialize-view
  (fn [{:keys [db]} [_ number subdivision project]]
    {:db       (assoc db
                      ::map {:markers []
                             :zoom    12
                             :center  nil
                             :tokens  {}}
                      ::point-search-results nil
                      ::application-create-mode :wizard.application-create-mode/default
                      ::create-application {:pending? true}
                      ::selected-subdivision subdivision
                      ::selected-municipality number
                      ::project project
                      ::municipality-domain-group-id nil
                      ::authority-group-id nil
                      ::authority-group-municipalities nil
                      ::route-permit-type nil
                      ::manual-input nil
                      ::manual-address nil
                      ::gis-error? false
                      ::selected-applicant nil)
     :dispatch [::gis-search/reset-view]}))

(rf/reg-event-db ::set-authority-group-id
  (fn [db [_ authority-group-id permit-type]]
    (-> db
        (assoc ::authority-group-id authority-group-id)
        (p/assoc-if ::route-permit-type permit-type))))


(rf/reg-event-fx ::load-wizard-config
  (fn [{:keys [db]} [_ country muni-id]]
    (let [country+muni-id [country muni-id]]
      ;; note that the fetch may be pending, if both ::municipalities-loaded and the page controller have both
      ;; triggered ::set-subdiv-and-municipality and due to luck, the db already contained municipality config
      ;; and ::load-wizard-config.success has not yet set ::permit-types-loaded to db. This does not matter much,
      ;; but may cause extra traffic. This could be refactored so that there is a separate ::only-set-subdiv-and-municipality
      ;; for the page controller that never triggers this fetch.
      (if (-> db ::permit-types-loaded (= country+muni-id))
        {:dispatch [::init-work-type-n-target]} ;; go straight to permit type init from query params
        {:comm/command {:command    :municipality/wizard-config
                        :data       {:country      country
                                     :municipality muni-id}
                        :method     :get
                        :on-success [::load-wizard-config.success country+muni-id]}}))))


(rf/reg-event-fx ::load-wizard-config.success
  (fn [{:keys [db]} [_ country+muni-id status {:keys [permit-types entities]}]]
    {:db (-> db
             (assoc ::permit-types-loaded country+muni-id)
             (assoc ::permit-type->groups permit-types))
     :fx [[:dispatch [::init-work-type-n-target]]
          [:dispatch [::entities/merge-entity-db status entities]]]}))

(rf/reg-event-fx ::load-wizard-config-by-authority-group
  (fn [{:keys [db]} _]
    (let [authority-group-id (::authority-group-id db)
          permit-type        (-> db ::route-permit-type)]
      (when (and authority-group-id permit-type)
        {:comm/command {:command    :municipality/wizard-config-by-authority-group
                        :data       {:authority-group-id authority-group-id
                                     :permit-type        permit-type}
                        :method     :get
                        :on-success [::load-wizard-config-by-authority-group.success]}}))))

(rf/reg-event-fx ::load-wizard-config-by-authority-group.success
  (fn [{:keys [db]} [_ status {:keys [permit-types entities municipalities domain-group-id]}]]
    (let [lang  (:language db)
          munis (->> municipalities
                     (sort-by (comp #(i18n-model/get-text % lang) :municipality/name)))]
      {:db (-> db
               (assoc ::permit-types-loaded :authority-group)
               (assoc ::permit-type->groups permit-types)
               (assoc ::authority-group-municipalities munis)
               (p/assoc-if ::municipality-domain-group-id domain-group-id))
       :fx (concat
             ;; Merge entities first so that ::init-work-type-n-target can read the
             ;; group-config entities (categories, work-types, etc.) when it runs.
             [[:dispatch [::entities/merge-entity-db status entities]]
              [:dispatch [::init-work-type-n-target]]]
             (when (seq munis)
               (let [{:municipality/keys [subdivision country number]} (first munis)
                     subdiv-or-country                                 (or subdivision country)]
                 [[:dispatch [::set-subdiv-and-municipality (name subdiv-or-country) number]]])))})))

(rf/reg-sub ::authority-group-id
  :-> ::authority-group-id)

(rf/reg-sub ::authority-group-municipalities
  :-> ::authority-group-municipalities)

(rf/reg-sub ::permit-types-loaded
  :-> ::permit-types-loaded)

(rf/reg-sub ::permit-types-loaded-for?
  :<- [::permit-types-loaded]
  (fn [loaded-for [_ country+muni-id]]
    (= loaded-for country+muni-id)))

(rf/reg-event-fx ::set-subdiv-and-municipality
  (fn [{:keys [db]} [_ subdivision-or-country muni-id]]
    (let [subdivision                      (some-> subdivision-or-country keyword)
          muni-config                      (find-municipality-config db muni-id subdivision
                                                                     (:domain-group-id (selected-authority-group db)))
          {:keys         [country]
           muni-group-id :domain-group-id} muni-config]
      (p/assoc-if {:db (p/assoc-if db
                                   ::selected-subdivision subdivision
                                   ::selected-municipality muni-id
                                   ::municipality-domain-group-id muni-group-id)}
                  ;; We get here if the user changes subdiv / municipality after the initial list has been loaded
                  ;; and thus need to fetch the map and wizard config for the selected group.
                  :fx (concat (when muni-group-id
                                [[:dispatch [::municipality/load-map-config muni-group-id]]])
                              (when (and country muni-id)
                                [[:dispatch [::load-wizard-config country muni-id]]]))))))


(rf/reg-event-fx ::municipalities-loaded
  (fn [{:keys [db]} _]
    ; This fires after municipality list has been initially loaded, subdivision and municipality may have been set
    ; from query parameters.
    (if (and (::authority-group-id db) (::route-permit-type db))
      ;; authority-group-id flow: skip default municipality, load wizard config by authority group
      {:db       (assoc-in db [::create-application :pending?] false)
       :dispatch [::load-wizard-config-by-authority-group]}
      (let [default-municipality (some->> db
                                          ::municipality/subdivisions+municipalities
                                          (mapcat second)
                                          (filter :default-for-wizard)
                                          first)
            default-subdivision  (or (:subdivision default-municipality)
                                     (:country default-municipality))
            municipality         (or (::selected-municipality db) (:municipality default-municipality))
            subdivision          (or (::selected-subdivision db) default-subdivision)]
        (p/assoc-if {:db (-> (assoc-in db [::create-application :pending?] false)
                             (p/assoc-if ::selected-municipality municipality
                                         ::selected-subdivision subdivision))}
                    :dispatch (when (and municipality subdivision)
                                ; Reset already set muni and subdivision to load additional config
                                [::set-subdiv-and-municipality subdivision municipality]))))))


(rf/reg-event-fx ::open-part
  (fn [{:keys [db]} [_ page-nr query-params]]
    {:route/push (p/assoc-if
                   {:route :create-application-v2 :params {:part page-nr}}
                   :query (p/assoc-if query-params
                                      :address (::manual-address db)
                                      :authority-group-id (::authority-group-id db)))}))


(defn location-not-required? [targets]
  (every? #(config-model/resolve-config-value :group.config/no-workspace-location %) targets))

(rf/reg-sub ::location-not-required
  (fn [[_ category work-types work-target] _]
    (->> (most-specific-targets category work-types work-target)
         (map #(rf/subscribe [::entities/entity %]))))
  :-> location-not-required?)


(defn allowed-parts [permit-type skip-project location-not-required?]
  (cond->> [1 2 3 4]
    (or (project-model/permit-types-without-project permit-type)
        skip-project)
    (filter #(not= % 2))
    location-not-required?
    (filter #(not= % 3))))

(rf/reg-sub ::location-not-required-for-route
  :<- [:route/match]
  (fn [{{{:keys [category work-types work-target]} :query} :parameters} _]
    (<sub [::location-not-required category work-types work-target])))

(rf/reg-sub ::allowed-parts
  :<- [:route/match]
  :<- [::location-not-required-for-route]
  (fn [[{{{:keys [permit-type skip-project]} :query} :parameters} location-not-required] _]
    (allowed-parts permit-type skip-project location-not-required)))


(rf/reg-event-fx ::open-previous-part
  (fn [_ [_ {{{:keys [permit-type skip-project]} :query
              {:keys [part]}                     :path} :parameters :as match} location-not-required]]
    (let [allowed-parts (allowed-parts permit-type skip-project location-not-required)
          prev-part     (->> (.indexOf allowed-parts part)
                             dec
                             (nth allowed-parts))]
      {:dispatch [::open-part prev-part (:query-params match)]})))


(rf/reg-event-fx ::open-next-part
  (fn [_ [_ {{{:keys [permit-type skip-project]} :query
              {:keys [part]}                     :path} :parameters :as match} location-not-required query-params]]
    (let [allowed-parts (allowed-parts permit-type skip-project location-not-required)
          next-part     (->> (.indexOf allowed-parts part)
                             inc
                             (nth allowed-parts))]
      {:dispatch [::open-part next-part (or query-params (:query-params match))]})))


(rf/reg-event-db ::set-gis-error
  (fn [db [_ v]]
    (assoc db ::gis-error? v)))


(rf/reg-sub ::gis-error?
  :-> ::gis-error?)


;;;
;;; Address/property
;;;


(rf/reg-sub ::address-result-pending
  :<- [::gis-search/pending?]
  :<- [::point-search-results]
  (fn [[addr-pending? point-results] _]
    (or addr-pending?
        (:pending? point-results))))


(rf/reg-sub ::address-search-term
  :<- [::gis-search/address-search-results]
  :-> :search-value)

(rf/reg-sub ::tr
  :<- [::selected-authority-group]
  :<- [::selected-permit-type]
  :<- [:tr]
  (fn [[group-like permit-type tr] _]
    (-> (group-model/get-i18n-mapping group-like)
        (merge (i18n-model/permit-type-specific-keys permit-type))
        (i18n-model/wrap-tr-fn tr))))


(s/defn location-for-create-app
  [{:keys [x y gis-id gis-layer address] :as location} :- property-selector/StrictLocationWizardParams]
  (let [property (select-keys location property-model/property-creation-keys)]
    (p/assoc-if
      {:geometry-point [x y]}
      :property  (not-empty property)
      :address   (when (nil? (address-model/address-checker address)) address)
      :gis-id    gis-id
      :gis-layer (keyword gis-layer)
      :geometry  (:geometry location))))


(defn location-from-query-params [params]
  (-> (select-keys params property-selector/location-keys-without-project)
      (update :address cljs.reader/read-string)
      (update :geometry cljs.reader/read-string)
      (update :gis-id cljs.reader/read-string)))


(defn location-column [{:keys [title value]}]
  [:div.flex.flex-col.mt-4.mr-4
   [:h5.font-normal title]
   [:span.font-bold value]])

(def location-property-keys
  [:property/id
   :property/secondary-id
   :property/integration-id
   :property/zoning
   :property/type-code
   :location/description])

(s/defn location-tile [tr location :- property-selector/LocationWizardParams]
  (into
    (cc/tile
      {:class-name "location-tile p-0"}
      [:div.location-column.self-center
       (cc/icon {:icon        i/location
                 :class-name  "ml-4"
                 :description "location"
                 :fill        "var(--ui-violet-100)"})]
      [:div.flex.flex-wrap.w-full.ml-4.mb-4
       (when (-> location :address address-model/address-checker nil?)
         [location-column {:title (tr [:application.property/address])
                           :value (-> location :address :address/full-address)}])
       (for-frag [key location-property-keys
                  :let [value (get location key)]
                  :when value]
         [location-column {:title (tr [key])
                           :value value}])])))


(rf/reg-event-fx ::selected-address-event
  (fn [{:keys [db]} [_ feature show-property-owners?]]
    (let [location-key-set     (set property-selector/location-keys-without-project)
          {:keys [parameters]} (:route/match db)
          group-id             (domain-group-id db)
          route-data           (->> feature
                                    (property-selector/make-location (::selected-municipality db) (::selected-subdivision db))
                                    (p/remove-keys nil?))]
      (p/assoc-if
        {:db         (dissoc db ::manual-address ::gis-error?)
         :route/push {:route  :create-application-v2
                      :params (get parameters :path)
                      :query  (merge ; wipes keys from url that were not returned from query
                                (p/remove-keys #(contains? location-key-set %) (get parameters :query))
                                route-data
                                (when (:address route-data)
                                  {:zoom 18}))}}
        :dispatch (when (and (municipality/check-owners? db group-id (:property feature))
                             show-property-owners?
                             (not= (:gis-layer route-data) :right-of-way-search))
                    [::property-selector/fetch-property-owners group-id feature])))))

(rf/reg-event-db ::set-manual-address
  (fn [db [_ value]]
    (assoc db ::manual-address value)))

(rf/reg-sub ::manual-address
  :-> ::manual-address)

(rf/reg-event-fx
  ::selected-location-event
  (fn [{:keys [db]} [_ feature show-property-owners?]]
    {:db           (dissoc db ::manual-address ::gis-error?)
     :comm/command (property-selector/point-search-command [(:x feature)
                                                            (:y feature)]
                                                           {:domain-group-id       (domain-group-id db)
                                                            :selected-municipality (::selected-municipality db)}
                                                           [::point-search.success show-property-owners?]
                                                           [::point-search.not-found])}))


(rf/reg-sub ::required-attrs
  :<- [::selected-subdivision]
  :<- [::selected-permit-type]
  (fn [[subdivision permit-type] _]
    (property-model/required-property-keys subdivision permit-type)))


(rf/reg-sub ::attr-required?
  :<- [::required-attrs]
  (fn [required-attrs [_ attr]]
    (contains? required-attrs attr)))


(defn manual-input-schema [required-attrs]
  (-> (st/merge ;; WIN-2009: Legal description mandatory for manual input
        property-selector/StrictLocationWizardParams
        (st/optional-keys
          (st/merge
            (st/select-keys property-selector/StrictLocationWizardParams [:property/id])
            {:property/legal schemas/NonBlankString})))
      (st/required-keys (vec required-attrs))))


(rf/reg-sub ::property-selected?
  :<- [:route/match]
  :<- [::manual-input?]
  :<- [::required-attrs]
  :<- [::entities/context]
  (fn [[{:keys [parameters]} manual-input? required-attrs ctx] _]
    (let [{{:keys          [permit-type category work-types work-target]
            :location/keys [description]
            :property/keys [type id]}                                    :query} parameters
          feature-data                                                           (s/check (if manual-input?
                                                                                            (manual-input-schema required-attrs)
                                                                                            property-selector/StrictLocationWizardParams)
                                                                                          (select-keys (get parameters :query) property-selector/location-keys-without-project))
          property-not-needed?                                                   (and feature-data
                                                                                      (contains? ptc-model/permit-types-without-mandatory-location permit-type)
                                                                                      (->> (most-specific-targets category work-types work-target)
                                                                                           (map (partial entities/find-entity ctx))
                                                                                           (every? (partial config-model/resolve-config-value :group.config/no-workspace-location))))
          ;; road segment can only be selected for :permit.type/PW
          public-works?                                                          (ptc-model/permit-types-with-row-layers permit-type)
          pw-permit-or-not-road-segment?                                         (or (and (not= type :property.type/road)
                                                                                          id)
                                                                                     public-works?)]
      (or property-not-needed?
          (and pw-permit-or-not-road-segment?
               (nil? feature-data))
          (and public-works? (or id (not (str/blank? description))))))))


(rf/reg-sub ::valid-address?
  (fn [db _]
    (or (-> db ::manual-address nil?)
        (some nil? [(address-model/address-checker (-> db :route/match :parameters :query :address cljs.reader/read-string))
                    (address-model/address-checker (-> db ::manual-address))]))))


(defn set-manual-entry-default-values [db location-data]
  (if (-> db ::manual-input :enabled?)
    (p/deep-merge db {::manual-input   {:values (select-keys location-data [:property/id :property/legal :address])}
                      ::manual-address (:address location-data)})
    db))


(rf/reg-event-fx ::point-search.success
  (fn [{:keys [db]} [_ show-property-owners? _ response]]
    (let [match            (:route/match db)
          location-key-set (set property-selector/location-keys-without-project)
          group-id         (domain-group-id db)
          route-data       (->> response
                                (property-selector/make-location (::selected-municipality db) (::selected-subdivision db))
                                (p/remove-keys nil?))]
      {:db         (-> db
                       (update ::point-search-results assoc :pending? false)
                       (set-manual-entry-default-values route-data))
       :dispatch-n (cond-> [[::property-selector/fetch-property-attributes group-id response]
                            [::set-manual-input-enabled false]
                            [::set-manual-input-value :location/description nil]]
                     (and (municipality/check-owners? db group-id (-> response :property))
                          show-property-owners?
                          (not= (:gis-layer response) :right-of-way-search))
                     (conj [::property-selector/fetch-property-owners group-id response]))
       :route/push {:route  :create-application-v2
                    :params (get match :path-params)
                    :query  (merge
                              (p/remove-keys #(contains? location-key-set %) (get match :query-params))
                              route-data)}})))


(rf/reg-event-fx ::point-search.not-found
  (fn [{:keys [db]} [_ [x y] gis-error?]]
    (let [match            (:route/match db)
          location-key-set (set property-selector/location-keys-without-project)]
      {:db         (-> (assoc-in db [::point-search-results :pending?] false)
                       (assoc ::gis-error? (boolean gis-error?)))
       :dispatch   [::set-manual-input-value :location/description nil]
       :route/push {:route  :create-application-v2
                    :params (get match :path-params)
                    :query  (merge (p/remove-keys #(contains? location-key-set %) (get match :query-params))
                                   {:x            x
                                    :y            y
                                    :address      {}
                                    :municipality (::selected-municipality db)
                                    :subdivision  (::selected-subdivision db)})}})))


(rf/reg-sub ::point-search-results
  :-> ::point-search-results)

(rf/reg-event-fx ::map-click
  (fn [{:keys [db]} [_ ^js map-click show-property-owners?]]
    (let [click-coordinates     (.-coordinate map-click)
          group-id              (domain-group-id db)
          selected-municipality (::selected-municipality db)
          layer-ids             (if (ff/enabled? :feature-flag.id/public-works-location-enhancements)
                                  (::layer-id-order db)
                                  [])]
      {:comm/command (property-selector/point-search-command click-coordinates
                                                             {:domain-group-id       group-id
                                                              :layer-ids             layer-ids
                                                              :selected-municipality selected-municipality}
                                                             [::point-search.success show-property-owners?]
                                                             [::point-search.not-found])
       :db           (-> db
                         (assoc-in [::point-search-results :pending?] true)
                         (assoc ::gis-error? false)
                         (dissoc ::manual-address)
                         (assoc-in [::map :markers] [(map-common-util/point-feature click-coordinates)])
                         (assoc-in [::map :center] click-coordinates))})))

(rf/reg-sub ::municipal-layers
  :<- [::domain-group-id]
  :<- [::municipality/municipality-config]
  :<- [::municipality/map-tokens]
  :<- [::selected-permit-type]
  (fn [[group-id config tokens selected-permit-type]]
    (municipality/municipal-layers config tokens group-id false (ptc-model/permit-types-with-row-layers selected-permit-type))))

(rf/reg-sub ::municipal-center
  :<- [::selected-municipality-data]
  :-> :map-center)

(rf/reg-sub ::custom-center
  #(-> % ::map :center))


(rf/reg-sub ::map-center
  :<- [::custom-center]
  :<- [::municipal-center]
  (fn [[custom-center municipal-center] _]
    (or custom-center
        municipal-center)))

(rf/reg-sub ::map-zoom
  (fn [db _]
    (-> db ::map :zoom)))



(defn update-map-from-params [db {:keys [x y zoom geometry]}]
  (let [geom-map         (-> geometry cljs.reader/read-string)
        property-feature (when-not (str/blank? geometry)
                           (let [coordinates (:coordinates geom-map)]
                             (case (:type geom-map)
                               "Polygon" (map-common-util/polygon-feature coordinates)
                               "MultiPolygon" (map-common-util/multi-polygon-feature coordinates)
                               (map-common-util/line-feature coordinates))))]
    (p/deep-merge :replace db {::map (p/assoc-if {:center            (when (and x y)
                                                                       [x y])
                                                  :markers           (if (and x y)
                                                                       [(map-common-util/point-feature #js [x y])]
                                                                       [])
                                                  :selected-property property-feature}
                                                 :zoom zoom)})))

(rf/reg-event-fx ::setup-location-from-parameters
  (fn [{:keys [db]} [_ {:keys [municipality subdivision] :as query-params}]]
    (p/assoc-if {:db (if-let [params (not-empty query-params)]
                       (-> (assoc db ::selected-municipality municipality)
                           (p/assoc-if ::selected-subdivision (some-> subdivision keyword))
                           (update-map-from-params params))
                       db)}
                :dispatch (when-not (-> db ::manual-input :enabled?)
                            [::gis-search/set-search-value (or (some-> query-params
                                                                       :address
                                                                       cljs.reader/read-string
                                                                       :address/full-address)
                                                               "")]))))

(rf/reg-sub ::show-property-owners?
  :<- [::selected-authority-group]
  :-> :automatic-property-owners?)

(rf/reg-sub ::property-owners
  (fn [[_ owner-key] _]
    [(rf/subscribe [::show-property-owners?])
     (rf/subscribe [::property-selector/property-owners owner-key])])
  (fn [[show-owners owners] _]
    (when show-owners
      owners)))

;; Temporary subscription which should be replaced with `::show-property-owners?`
;; when :feature-flag.id/automatic-property-owners-for-authorities is not needed
;; anymore. When the feature flag is off, there are cases when `::show-property-owners?`
;; returns false  although before the flag it returned true.
(rf/reg-sub ::show-property-owners-for-location?
  :<- [::selected-authority-group]
  :-> :show-owners-in-location-phase?)

;; Temporary subscription similar to the one above
(rf/reg-sub ::property-owners-for-location
  (fn [[_ owner-key] _]
    [(rf/subscribe [::show-property-owners-for-location?])
     (rf/subscribe [::property-selector/property-owners owner-key])])
  (fn [[show-owners owners] _]
    (when show-owners
      owners)))

(rf/reg-sub ::config-loaded?
  :<- [::permit-type->groups]
  :-> some?)

(rf/reg-sub ::pre-consultation-preference
  :<- [::category-group-configs]
  :<- [::selected-category]
  (fn [[categories selected-category] _]
    (some-> (p/find-first categories {:db/id selected-category})
            (get :group.config/pre-consultation config-model/default-pre-consultation))))

(rf/reg-sub ::pre-consultation-selected?
  :-> ::create-pre-consultation?)

(rf/reg-sub ::create-pre-consultation?
  :<- [::pre-consultation-preference]
  :<- [::pre-consultation-selected?]
  (fn [[preference selection] _]
    (case preference
      :group.config.pre-consultation/not-applicable false
      :group.config.pre-consultation/optional       (if (some? selection) selection false)
      :group.config.pre-consultation/required       true
      false)))

(rf/reg-event-db ::set-create-pre-consultation
  (fn [db [_ value]]
    (assoc db ::create-pre-consultation? value)))

(rf/reg-event-fx ::set-create-mode-query-param
  (fn [_ [_ value]]
    {:dispatch [::routing/merge-query-params (merge {:create-mode (name value)}
                                                    (when (not= value :wizard.application-create-mode/default)
                                                      {:applicant-type nil
                                                       :applicant-id   nil}))]}))

(rf/reg-event-db ::set-application-create-mode
  (fn [db [_ value]]
    (assoc db
           ::application-create-mode
           (if value
             (keyword "wizard.application-create-mode" value)
             :wizard.application-create-mode/default))))

(rf/reg-sub ::application-create-mode
  :-> ::application-create-mode)

(rf/reg-event-fx ::set-submit-ts-query-param
  (fn [_ [_ ^js value]]
    (when (inst? value)
      {:dispatch [::routing/merge-query-params {:submit-ts (.getTime value)}]})))

(rf/reg-event-db ::set-manual-submit-date
  (fn [db [_ create-mode value]]
    (if (and (= create-mode "submitted-earlier")
             value)
      (assoc db ::manual-submit-date (js/Date. value))
      (dissoc db ::manual-submit-date))))

(rf/reg-sub ::manual-submit-date
  :-> ::manual-submit-date)

(defn manual-submit-date-valid? [date]
  (date-util/on-or-before? date (date-util/now)))

(rf/reg-sub ::create-mode-ok?
  :<- [::application-create-mode]
  :<- [::manual-submit-date]
  (fn [[mode date]]
    (or (not= mode :wizard.application-create-mode/submitted-earlier)
        (manual-submit-date-valid? date))))

(rf/reg-event-fx ::select-applicant
  (fn [_ [_ value]]
    {:dispatch [::routing/merge-query-params {:applicant-type (if (:business/unique-id value)
                                                                "business"
                                                                "self")
                                              :applicant-id   (or (:business/unique-id value)
                                                                  (:user/username value))}]}))

(rf/reg-event-db ::set-selected-applicant-id
  (fn [db [_ applicant-type applicant-id]]
    (if applicant-type
      (assoc db
             ::selected-applicant-id
             (if (= applicant-type "business")
               {:business/unique-id applicant-id}
               {:user/username applicant-id}))
      (dissoc db ::selected-applicant-id))))

(rf/reg-sub ::selected-applicant-id :-> ::selected-applicant-id)

(rf/reg-sub ::applicant-selection-ok?
  :<- [::selected-applicant]
  :<- [::show-my-businesses?]
  :<- [::my-businesses-loading?]
  (fn [[applicant show-my-businesses? businesses-loading?]]
    (or (and (not businesses-loading?)
             (not show-my-businesses?))
        (boolean applicant))))

;;;
;;; Project
;;;


(rc/reg-chain ::load-user-projects
  (fn [{:keys [db]} _]
    {:db           (update db ::create-application assoc :pending-projects? true)
     :comm/command {:command :project/find}})
  (fn [{:keys [db]} [_ status response]]
    {:db       (update db ::create-application assoc :pending-projects? false)
     :dispatch [::entities/merge-entity-db status response]}))


(rf/reg-event-fx ::set-route-state
  (fn [_ [_ part query-params]]
    {:route/push {:route :create-application-v2 :params {:part part} :query query-params}}))


(rf/reg-sub ::project-ok?
  :<- [:route/match]
  (fn [{{{:keys         [permit-type]
          :project/keys [id name]}    :query} :parameters}]
    (or id
        (not (str/blank? name))
        (project-model/permit-types-without-project permit-type))))

;;
;; Permit type
;;

(rf/reg-sub ::permit-type->groups
  :-> ::permit-type->groups)

(rf/reg-sub ::permit-types
  :<- [::permit-type->groups]
  :<- [:tr]
  (fn [[permit-type->groups tr] [_ road-segment?]]
    (->> (if road-segment?
           [:permit-type/PW]
           (keys permit-type->groups))
         (sort-by #(tr [%]))
         (remove ptc-model/additional-permit-type?)
         (remove ptc-model/hidden-permit-types))))

(rf/reg-sub ::permit-types-with-project
  :<- [::permit-types]
  (fn [pts _]
    (->> pts
         (remove project-model/permit-types-without-project))))

(rf/reg-sub ::selected-permit-type
  :-> ::selected-permit-type)

(rf/reg-sub ::multiple-work-types?
  :-> (comp ptc-model/permit-types-with-multiple-work-types ::selected-permit-type))

(rf/reg-sub ::project
  :-> ::project)

(rf/reg-sub ::selected-category
  :-> ::selected-category)

(rf/reg-sub ::selected-work-types :-> ::selected-work-types)

(rf/reg-sub ::selected-permit-type-groups
  :<- [::permit-type->groups]
  :<- [::selected-permit-type]
  (fn [[permit-type->groups permit-type] _]
    (permit-type-groups permit-type->groups permit-type)))

(rf/reg-sub ::selected-authority-group
  :<- [::selected-permit-type-groups]
  :<- [::selected-category]
  (fn [[groups category]]
    (authority-group-for-category groups category)))


(rf/reg-sub ::authority-user?
  :<- [::selected-authority-group]
  :-> :authority?)


(defn- find-root-configs [ctx permit-type authority-group-ids]
  (entities/find-entities ctx
                          (filter (every-pred (comp (partial = permit-type) :group.config/target)
                                              (comp (set authority-group-ids) :group.config/group)
                                              du/is-active?))))


(rf/reg-sub ::selected-permit-type-group-config
  :<- [::entities/context]
  :<- [::selected-authority-group]
  :<- [::selected-permit-type]
  (fn [[ctx authority-group permit-type] _]
    (when (and authority-group permit-type)
      (-> (find-root-configs ctx permit-type #{(:db/id authority-group)})
          first))))


(rf/reg-sub ::all-permit-type-group-configs
  :<- [::entities/context]
  :<- [::permit-type->groups]
  :<- [::selected-permit-type]
  (fn [[ctx permit-type->groups permit-type] _]
    (when (and permit-type->groups permit-type)
      (->> permit-type->groups
           permit-type
           (map :db/id)
           (find-root-configs ctx permit-type)))))


(rf/reg-sub ::category-group-configs
  :<- [::all-permit-type-group-configs]
  :<- [:language]
  :<- [::authority-user?]
  (fn [[parent-configs language authority?] _]
    (let [filter-fn (every-pred
                      du/is-active?
                      (partial config-model/allow-workspace-creation? authority?))]
      (p/condas-> (->> parent-configs (mapcat :entity/_parent) (filter filter-fn)) cats
        ;; If any project types are defined, show only categories with project types
        (some (comp seq :group.config/project-types) cats)
        (filter :group.config/project-types cats)

        :always
        (config-model/sort-group-configs language cats)))))


(defn child-group-configs [ctx language parent-id authority?]
  (->> (entities/find-entities ctx {:entity/parent parent-id
                                    :entity/type   :entity.type/group-config})
       (filter (every-pred du/is-active?
                           (partial config-model/allow-workspace-creation? authority?)))
       (config-model/sort-group-configs language)))


(rf/reg-sub ::child-group-configs
  :<- [::entities/context]
  :<- [:language]
  :<- [::authority-user?]
  (fn [[ctx language authority?] [_ parent-id]]
    (child-group-configs ctx language parent-id authority?)))


(rf/reg-sub ::selected-category-children
  :<- [::entities/context]
  :<- [:language]
  :<- [::selected-category]
  :<- [::authority-user?]
  (fn [[ctx language category-id authority?] _]
    (when category-id
      (child-group-configs ctx language category-id authority?))))


(rf/reg-sub ::selected-work-type-children
  :<- [::entities/context]
  :<- [:language]
  :<- [::selected-work-types]
  :<- [::authority-user?]
  (fn [[ctx language wt-ids authority?] _]
    (when wt-ids
      (mapcat #(child-group-configs ctx language % authority?) wt-ids))))


(rf/reg-sub ::sorted-project-types
  :<- [::category-group-configs]
  :<- [:tr]
  (fn [[configs tr] _]
    (->> configs
         (mapcat :group.config/project-types)
         distinct
         ;; leave type "Other" last in the list
         (sort (fn [f s]
                 (cond (= f :project-type/other) 1
                       (= s :project-type/other) -1
                       :else (compare (tr [f]) (tr [s]))))))))

(rf/reg-sub ::permit-type-descriptions
  :<- [::permit-type->groups]
  :<- [::selected-authority-group]
  (fn [[permit-type->groups selected-group] _]
    (->> permit-type->groups
         (p/map-vals (fn [groups]
                       (let [group (or (p/find-first groups selected-group)
                                       (p/find-first groups :permit-type-config/permit-type-descriptions)
                                       (first groups))]
                         (when (:permit-type-config/permit-type-descriptions group)
                           (select-keys group [:db/id :permit-type-config/permit-type-descriptions]))))))))

(rf/reg-sub ::permit-type-names
  :<- [::entities/context]
  :<- [::permit-type->groups]
  (fn [[ctx permit-type->groups] _]
    (->> permit-type->groups
         (p/map-entries (fn [[permit-type groups]]
                          [permit-type
                           (some->> groups
                                    (map :db/id)
                                    (find-root-configs ctx permit-type)
                                    first
                                    :group.config/names)])))))




(rf/reg-event-fx ::select-project-type
  (fn [{:keys [db]} [_ project-type]]
    (let [{:keys [parameters]} (:route/match db)]
      {:route/push {:route  :create-application-v2
                    :params (get parameters :path)
                    :query  (-> (get parameters :query)
                                (assoc :project-type project-type)
                                (dissoc :category)
                                (dissoc :work-types)
                                (dissoc :work-target))}})))

(rf/reg-event-fx ::select-permit-category
  (fn [{:keys [db]} [_ category]]
    (let [{:keys [parameters]} (:route/match db)]
      {:route/push {:route  :create-application-v2
                    :params (get parameters :path)
                    :query  (-> (get parameters :query)
                                (assoc :category category)
                                (dissoc :work-types)
                                (dissoc :work-target))}})))

(rf/reg-event-fx ::select-work-type
  (fn [{:keys [db]} [_ work-type]]
    (let [{:keys [query path]} (-> db :route/match :parameters)
          work-types           (:work-types query)
          single-work-type?    (-> db ::selected-permit-type ptc-model/permit-types-with-multiple-work-types not)
          selected             (cond
                                 single-work-type?
                                 [work-type]

                                 (some (partial = work-type) work-types)
                                 (vec (remove (partial = work-type) work-types))

                                 :else
                                 (conj work-types work-type))
          query-args           (cond-> query
                                 (empty? selected)
                                 (dissoc :work-types)

                                 (not (empty? selected))
                                 (assoc :work-types selected)

                                 true
                                 (dissoc :work-target))]
      {:route/push {:route  :create-application-v2
                    :params path
                    :query  query-args}})))

(rf/reg-event-fx ::select-work-target
  (fn [_ [_ work-target]]
    {:dispatch [::routing/merge-query-params {:work-target work-target}]}))

(rf/reg-sub ::permit-details-selected?
  :<- [:route/match]
  :<- [::applicant-selection-ok?]
  :<- [::create-pre-consultation?]
  :<- [::selected-category-children]
  :<- [::selected-work-type-children]
  (fn [[{{{:keys [permit-type category work-types work-target]} :query} :parameters}
        applicant-selection-ok?
        create-pre-consultation?
        available-work-types
        available-work-targets]]
    (and (keyword? permit-type)
         category
         applicant-selection-ok?
         (or create-pre-consultation?
             (and (or (seq work-types)
                      (not (seq available-work-types)))
                  (or (not (str/blank? work-target))
                      (not (seq available-work-targets))))))))


(defn map-config-to-load
  "Domain group whose map configuration still needs to be loaded, if any.
   ::set-subdiv-and-municipality loads the config of the municipality default domain group, which is
   not necessarily the one handling the selected permit type + category."
  [db]
  (let [group-id (domain-group-id db)]
    (when (and group-id
               (not (municipality/map-config-loaded? db group-id)))
      group-id)))


(rf/reg-event-fx ::ensure-map-config
  (fn [{:keys [db]} _]
    (when-let [group-id (map-config-to-load db)]
      {:dispatch [::municipality/load-map-config group-id]})))


(rf/reg-event-fx ::init-work-type-n-target
  (fn [{:keys [db]} _]
    (let [{{{:keys [permit-type category work-types work-target project-type] :as query} :query
            {:keys [part]}                                                               :path} :parameters} (:route/match db)]
      (when (or (>= part 3)
                permit-type)
        (p/if-all-let [permit-type->groups (::permit-type->groups db)
                       permit-types        (if (= (:property/type query) :property.type/road)
                                             #{:permit-type/PW}
                                             (keys permit-type->groups))]
          (let [permit-type            (or (str-util/blank->nil (keyword permit-type))
                                           (-> permit-types
                                               sort
                                               first))
                root-configs           (->> permit-type->groups
                                            permit-type
                                            (map :db/id)
                                            (find-root-configs {:entity-db  (::entities/entity-db db)
                                                                :components (::entities/components db)
                                                                :schema     (::entities/schema db)}
                                                               permit-type))
                available-categories   (cond->> (->> root-configs
                                                     (mapcat :entity/_parent))
                                         project-type (filter (config-model/in-project-type-fn project-type)))
                init-category          (cond
                                         (not (str/blank? category))
                                         (p/find-first available-categories {:db/id category})

                                         (= 1 (count available-categories)) (first available-categories))
                available-work-types   (->> init-category
                                            :entity/_parent
                                            (filter (du/in-state-fn :group-config.state/enabled)))
                work-type-init         (cond
                                         (not (empty? work-types))
                                         (map #(p/find-first available-work-types {:db/id %}) work-types)

                                         (= 1 (count available-work-types)) available-work-types

                                         :else (some-> (:group.config/default-work-type-gc init-category) vector))
                available-work-targets (->> work-type-init
                                            (mapcat :entity/_parent)
                                            (filter (du/in-state-fn :group-config.state/enabled)))
                work-target-init       (cond
                                         (not (str/blank? work-target))
                                         (p/find-first available-work-targets {:db/id work-target})

                                         (= 1 (count available-work-targets)) (first available-work-targets)
                                         :else (:group.config/default-work-target-gc init-category))]
            {:fx [[:dispatch [::routing/merge-query-params (p/assoc-if {}
                                                                       :permit-type permit-type
                                                                       :category (:db/id init-category)
                                                                       :work-types (some->> work-type-init
                                                                                            (keep :db/id)
                                                                                            seq)
                                                                       :work-target (:db/id work-target-init))
                              :route/replace]]
                  ;; Permit type / category determine the authority group and thus the domain group
                  ;; whose map configuration is used in the location phase.
                  [:dispatch [::ensure-map-config]]]
             :db (assoc db
                        ::selected-permit-type permit-type
                        ::selected-category (:db/id init-category)
                        ::selected-work-types (->> (map :db/id work-type-init)
                                                   (filter some?)
                                                   vec))}))))))


(rf/reg-event-fx ::fetch-my-businesses
  (fn [{:keys [db]} _]
    {:comm/command {:command          :business/my-businesses
                    :method           :get
                    :on-success       [::fetch-my-businesses.success]
                    :finally-dispatch [::fetch-my-businesses.done]}
     :db           (assoc db ::my-businesses-loading? true)}))


(rf/reg-event-db ::fetch-my-businesses.success
  (fn [db [_ _ results]]
    (assoc db ::my-businesses results)))


(rf/reg-event-db ::fetch-my-businesses.done
  #(dissoc % ::my-businesses-loading?))


(rf/reg-sub ::my-businesses :-> ::my-businesses)


(rf/reg-sub ::applicant-select-opts
  :<- [::my-businesses]
  :<- [:session/user]
  :<- [:language]
  :<- [:tr]
  (fn [[businesses user lang tr] _]
    (->> businesses
         (map (fn [business]
                (merge (select-keys business [:business/unique-id])
                       {:name (str (business-model/format-name business lang)
                                   " (" (tr [:party.type/business]) ")")})))
         (sort-by :name)
         (concat [(merge (select-keys user [:user/username])
                         {:name (str (tr [:wizard/myself]) " (" (user-model/full-name user) ")")})]))))

(rf/reg-sub ::selected-applicant
  :<- [::applicant-select-opts]
  :<- [::selected-applicant-id]
  (fn [[options selection]]
    (p/find-first options selection)))

(rf/reg-sub ::my-businesses-loading? :-> ::my-businesses-loading?)

(rf/reg-sub ::show-my-businesses?
  :<- [::selected-permit-type]
  :<- [::application-create-mode]
  :<- [::my-businesses]
  :<- [::ff/enabled? :feature-flag.id/business-parties-all-permit-types]
  (fn [[permit-type create-mode my-businesses all-permit-types-enabled?] _]
    (boolean (and (business-model/business-parties-enabled? permit-type all-permit-types-enabled?)
                  (= create-mode :wizard.application-create-mode/default)
                  (seq my-businesses)))))

;;;
;;;  Application
;;;

(rf/reg-event-fx ::create-application-success
  (fn [_ [_ status response]]
    (let [workspace-id (entities/property-of-some-in-apply-changes :workspace/id response)]
      {:fx [[:dispatch [::entities/apply-changes status response]]
            [:route/push {:route :view/workspace :params {:workspace-id workspace-id}}]]})))


(rf/reg-event-db ::create-application.done
  (fn [db _]
    (assoc-in db [::create-application :pending?] false)))


(rf/reg-event-fx ::create-application
  (fn [{:keys [db]} [_ create-pre-consultation?]]
    (let [{:keys [parameters]} (:route/match db)
          mode                 (::application-create-mode db)
          query-params         (:query parameters)
          with-project?        (not (project-model/permit-types-without-project (:permit-type query-params)))
          ;; Only :municipality and :country are used, they are the same in every entry.
          municipality         (find-municipality-config db
                                                         (::selected-municipality db)
                                                         (::selected-subdivision db))
          skip-location?       (->> (most-specific-targets (:category query-params)
                                                           (:work-types query-params)
                                                           (:work-target query-params))
                                    (map (partial entities/find-entity (entities/db->context db)))
                                    location-not-required?)
          app-data             (p/assoc-if
                                 (merge
                                   (when-not skip-location?
                                     (location-for-create-app (location-from-query-params query-params)))
                                   (select-keys municipality [:municipality :country])
                                   {:permit-type (get query-params :permit-type)
                                    :category-id (get query-params :category)})
                                 :project/id (when with-project? (:project/id query-params))
                                 :location/description (:location/description query-params)
                                 :project/name (when with-project? (str-util/blank->nil (:project/name query-params)))
                                 :create-without-party? (contains? #{:wizard.application-create-mode/behalf-of-customer
                                                                     :wizard.application-create-mode/submitted-earlier}
                                                                   mode)
                                 :create-pre-consultation? create-pre-consultation?
                                 :work-type-ids (get query-params :work-types)
                                 :work-target-id (get query-params :work-target)
                                 :submitted (when (= mode :wizard.application-create-mode/submitted-earlier)
                                              (::manual-submit-date db))
                                 :business/unique-id (some-> db ::selected-applicant-id :business/unique-id))]
      {:comm/command {:command          :application/create
                      :data             app-data
                      :on-success       [::create-application-success]
                      :finally-dispatch [::create-application.done]}
       :db           (assoc-in db [::create-application :pending?] true)})))


(rf/reg-sub ::create-application-data
  :-> ::create-application)


(rf/reg-sub ::pending?
  :<- [::create-application-data]
  :-> (fn [{:keys [pending? pending-projects?]}]
        (or pending? pending-projects?)))


(rf/reg-sub ::ready-to-create?
  :<- [::project-ok?]
  :<- [::property-selected?]
  :<- [::permit-details-selected?]
  (fn [[project-ok? location-ok? permit-type-ok?]]
    (and project-ok? location-ok? permit-type-ok?)))

(rf/reg-sub ::can-finish-creation?
  :<- [::ready-to-create?]
  :<- [::create-mode-ok?]
  :<- [::pending?]
  (fn [[ready-to-create? create-mode-ok? pending?]]
    (and ready-to-create?
         create-mode-ok?
         (not pending?))))

(rf/reg-event-db ::set-manual-input-enabled
  (fn [db [_ enabled?]]
    (assoc-in db [::manual-input :enabled?] enabled?)))

(rf/reg-event-fx ::set-manual-input-value
  (fn [{:keys [db]} [_ k v]]
    {:db       (assoc-in db [::manual-input :values k] v)
     :dispatch [::routing/merge-query-params {k v} false true]}))

(rf/reg-sub ::manual-input
  :-> ::manual-input)

(rf/reg-sub ::manual-input?
  :<- [::manual-input]
  :-> :enabled?)

(rf/reg-sub ::manual-input-values
  :<- [::manual-input]
  :-> :values)

;;;
;;; Components
;;;

(def wizard-button-container :div.mt-6>div.flex.justify-end.border-t.border-gray-400.border-solid.pt-6)

(defn wizard-buttons
  "Buttons to be displayed on the bottom of the Creation wizard section."
  [tr {:keys [next back]}]
  [wizard-button-container
   (cc/button
     {:kind :ghost
      :href (href/href :dashboard)}
     (tr [:cancel]))
   (cc/button
     (merge
       {:kind       :tertiary
        :class-name "ml-2"}
       (dissoc back :text))
     (or (:text back) (tr [:back])))
   (cc/button
     (merge
       {:id               :create-wizard-next
        :class-name       "ml-2"
        :icon             i/arrow-right
        :icon-description (tr [:next])
        :icon-position    :right}
       (dissoc next :text))
     (or (:text next) (tr [:next])))])

(defn address-editor [tr subdivision & {:keys [optional?]}]
  [workspace-address/address-input-fields
   (util/mmap {:set-addr-evt ::set-manual-address
               :optional?    (boolean optional?)
               :address      (<sub [::manual-address])}
              tr subdivision)])

(def location-title :h4.mb-2)
(def location-item :p.mb-1.pb-0)

(defn property-details [tr location]
  [:div.mb-6
   [location-title (tr [:wizard/property-details])]
   (when (= :property.type/road (:property/type location))
     [location-item (str (tr [:application.property/type]) ": "
                         (tr [:application.property/road]))])
   (when (= :property.type/road (:property/type location))
     [location-item (str (tr [:property/description]) ": "
                         (or (:property/description location)
                             (tr [:property/from-to]
                                 [(:property/from-st location)
                                  (:property/to-st location)])))])
   (for-frag [key [:property/id :property/secondary-id :property/integration-id :property/type-code]]
     (when-let [v (key location)]
       (when (or (not= key :property/id) (not= :property.type/road (:property/type location)))
         [location-item (str (tr [key]) ": "
                             (if (= key :property/type-code)
                               (property-model/property-type-text tr
                                                                  v
                                                                  (first (str/split (:municipality location) #"-")))
                               v))])))])

(defn other-property-information [tr location]
  (let [other-keys      [:property/ward :property/zoning :property/legal]
        property-owners (<sub [::property-owners-for-location (property-selector/owner-cache-key location)])
        attributes      (<sub [::property-selector/property-attributes])]
    (when (or (some #(% location) other-keys) (seq property-owners))
      [:div
       [location-title (tr [:wizard/other-information])]
       (for-frag [key other-keys]
         (when-let [v (key location)]
           [location-item (str (tr [(keyword "property" (name key))]) ": " v)]))
       (when (seq property-owners)
         [location-item (str (tr [(if (next property-owners)
                                    :application.property/owners
                                    :application.property/owner)])
                             ": "
                             (->> property-owners
                                  (map :name)
                                  (str/join ", ")))])
       [:div [property-attribute/attribute-tags attributes]]])))

(defn selected-location [tr {:keys [subdivision] :as location} enhanced-pw?]
  [:div
   (when-not (= :property.type/road (:property/type location))
     [:div.mb-6.mt-3.lg:mt-0
      [location-title (tr [:workspace/address])]
      (if (nil? (address-model/address-checker (:address location)))
        [location-item (-> location :address :address/full-address)]
        [:div
         [location-item (if (and enhanced-pw?
                                 (not (:property/type location)))
                          (tr [:wizard/address-not-found-describe-location])
                          (tr [:wizard/address-not-found]))]
         [address-editor tr subdivision]])])
   [property-details tr location]
   [other-property-information tr location]])

(defn location-attr [{:keys [tr attr values]}]
  [:div.mt-3
   [ui/text-input {:label-text (tr [attr])
                   :value      (get values attr)
                   :required   (<sub [::attr-required? attr])
                   :on-change  #(>evt [::set-manual-input-value attr (futil/tval %)])}]])

(defn manual-address-entry [tr feature-data]
  (let [values (<sub [::manual-input-values])]
    [:div
     (if-not (:x feature-data)
       [:div.font-semibold.mb-6.mt-3.lg:mt-0
        (tr [:wizard/manual-entry-point-info])]
       [:<>
        [:div.mb-6.mt-3.lg:mt-0
         [location-title (tr [:workspace/address])]
         [address-editor tr (<sub [::selected-subdivision])]]
        [:div.mb-6
         [location-title (tr [:wizard/property-details])]
         [location-attr (util/mmap {:attr :property/id} tr values)]
         [location-attr (util/mmap {:attr :property/legal} tr values)]]])]))

(defn location-description [{:keys [attr tr values]}]
  [ui/text-area {:label-text (tr [attr])
                 :value      (get values attr)
                 :required   true
                 :on-change  #(>evt [::set-manual-input-value attr (futil/tval %)])}])

(defn manual-location-entry [tr feature-data]
  (let [values (<sub [::manual-input-values])]
    [:div
     (if-not (:x feature-data)
       [:div.font-semibold.mb-6.mt-3.lg:mt-0
        (tr [:wizard/manual-entry-point-info])]
       [:<>
        [:div.mb-6.mt-3.lg:mt-0
         [location-title (tr [:wizard/location])]
         [address-editor tr (<sub [::selected-subdivision]) {:optional? true}]]
        [:div.mb-6
         [location-description (util/mmap {:attr :location/description} tr values)]]
        [location/coordinates tr feature-data]])]))

(def link-button :button.bg-transparent.border-0.p-0.text-ui-blue.hover:underline)

(defn missing-property-data [tr]
  (let [gis-error? (<sub [::gis-error?])]
    [:div.p-4
     [:div.mb-6
      [:h3 (tr [(if gis-error?
                  :wizard/gis-error-title
                  :wizard/location-not-found-title)])]
      [:p (tr [(if gis-error?
                 :wizard/gis-error-description
                 :wizard/location-not-found-description)])]
      [:p [link-button
           {:on-click #(>evt [::set-manual-input-enabled true])}
           (tr [:wizard/start-manual-entry])]]]]))

(defn- get-default-map-center
  "Returns the average center coordinate for the whole operating area"
  []
  ;; TODO: replace with default center coordinate in config file
  (->> (<sub [::municipality/municipalities])
       (map :map-center)
       municipality-model/center-of-centers))

(defn map-wrapper-workspace-map [col-size disabled-help show-property-owners? geometry]
  [:div {:class-name (str "bx--col-xs-12 bx--col-lg-" col-size)
         :style      {:max-height "50vh" :min-height "400px"}}
   [workspace-map/workspace-map (merge {:layers             (<sub [::municipal-layers])
                                        :propertyGeometries {:primaryPropertyGeometry (property-model/geojson-feature geometry)}
                                        :extended?          (ff/enabled? :feature-flag.id/public-works-location-enhancements)
                                        :layerOpacity       0.7
                                        :setLayerIdOrder    #(>evt [::set-layer-id-order %])
                                        :events             [{:eventType     "singleclick"
                                                              :eventFunction #(>evt [::map-click % show-property-owners?])}]}
                                       (if disabled-help
                                         {:center (get-default-map-center)
                                          :zoom   4}
                                         {:center (<sub [::map-center])
                                          :zoom   (<sub [::map-zoom])}))]
   (when disabled-help
     [:div.absolute.top-0.bottom-0.left-0.right-0
      [:div.mx-2.pl-12.p-3.cursor-not-allowed.bg-ui-white.opacity-75.absolute.top-0.bottom-0.left-0.right-0]
      [:div.absolute.top-0.bottom-0.left-0.right-0.text-center
       [:div.pt-4.bold.text-ui-night-sky.text-lg
        disabled-help]]])])

(rf/reg-sub ::selected-municipality
  :-> ::selected-municipality)

(rf/reg-sub ::selected-subdivision
  :-> ::selected-subdivision)

(rf/reg-sub ::show-subdiv-select?
  :<- [::subdivisions]
  (fn [all-subdivisions _]
    (-> all-subdivisions count (> 1))))

(rf/reg-sub ::currently-active-subdivision
  :<- [::selected-subdivision]
  :<- [::show-subdiv-select?]
  :<- [::selected-municipality-data]
  :<- [::subdivisions]
  (fn [[selected-subdiv show-select? selected-muni all-subdivisions]]
    (or selected-subdiv
        (some-> selected-muni :subdivision)
        (when-not show-select?
          (first all-subdivisions)))))

(rf/reg-sub ::distinct-municipalities-in-subdivision
  :<- [::municipality/subdivisions+municipalities]
  :<- [::currently-active-subdivision]
  (fn [[subdivs+munis selected-subdiv] _]
    (->> (get subdivs+munis selected-subdiv)
         ;; There can actually be multiple entries of the same municipality for different domain groups serving the
         ;; same area (upper-tier + lower-tier).
         ;; We are only interested in name and number for the preliminary selection.
         (map #(select-keys % [:municipality-name :municipality]))
         distinct)))

(rf/reg-sub ::authority-group-domain-group-id
  :<- [::selected-authority-group]
  :-> :domain-group-id)

;; Of the entries of the selected municipality, the one belonging to the domain group of the
;; authority group that handles the selected permit type + category. See `domain-group-id`.
(rf/reg-sub ::selected-municipality-data
  :<- [::selected-municipality]
  :<- [::selected-subdivision]
  :<- [::municipality/subdivisions+municipalities]
  :<- [::authority-group-domain-group-id]
  (fn [[muni-id subdiv subdivs+munis ag-domain-group-id] _]
    (-> (municipality-configs subdivs+munis muni-id subdiv)
        (pick-municipality-config ag-domain-group-id))))

(rf/reg-sub ::municipality-domain-group-id
  :-> ::municipality-domain-group-id)

;; Domain group that owns the GIS configuration used for map layers and property search,
;; see `domain-group-id`.
(rf/reg-sub ::domain-group-id
  :<- [::selected-municipality-data]
  :<- [::municipality-domain-group-id]
  (fn [[muni-config fallback-id] _]
    (or (:domain-group-id muni-config)
        fallback-id)))

(rf/reg-sub ::subdivisions
  :<- [::municipality/subdivisions+municipalities]
  :<- [:tr]
  (fn [[subdivs+munis tr] _]
    (->> subdivs+munis
         (map first)
         (sort-by (partial municipality-model/subdivision-name tr)))))

(rf/reg-sub ::layer-id-order
  :-> ::layer-id-order)

(rf/reg-event-fx ::set-layer-id-order
  (fn [{:keys [db]} [_ layer-ids]]
    {:db (assoc db ::layer-id-order layer-ids)}))

(rf/reg-event-fx ::municipality-selected
  (fn [{:keys [db]} [_ selected-subdiv value]]
    {:db         (p/deep-merge :replace db {::map {:zoom   12
                                                   :center nil}})
     :dispatch-n [[::gis-search/clear-address-search]
                  [::set-subdiv-and-municipality selected-subdiv value]]}))

(rf/reg-event-fx ::authority-group-municipality-selected
  (fn [{:keys [db]} [_ {:municipality/keys [number subdivision country]}]]
    (let [subdiv-or-country (or subdivision country)]
      {:db         (p/deep-merge :replace db {::map {:zoom   12
                                                     :center nil}})
       :dispatch-n [[::gis-search/clear-address-search]
                    [::set-subdiv-and-municipality (name subdiv-or-country) number]]})))

(rf/reg-event-fx ::subdivision-selected
  (fn [{:keys [db]} [_ value]]
    {:db         (-> (dissoc db ::selected-municipality)
                     (dissoc ::municipality-domain-group-id))
     :dispatch-n [[::gis-search/clear-address-search]
                  [::set-subdiv-and-municipality value nil]]}))

(rf/reg-event-fx ::start-create
  (fn [{:keys [db]} [_ number subdivision project]]
    {:db (assoc db ::start-create? true)
     :fx [[:dispatch [::initialize-view number subdivision project]]
          [:dispatch [::municipality/load-municipalities true [::municipalities-loaded]]]]}))

(rf/reg-event-fx ::close-create
  (fn [{:keys [db]} _]
    {:db       (dissoc db ::start-create?)
     :dispatch [::routing/replace-query-params {} :route/replace]}))

(rf/reg-event-fx ::open-preliminary-selections
  ;; Minimal version of ::start-create for use with new application-wizard
  (fn [{:keys [db]} [_ municipality-number subdivision]]
    {:db (assoc db
                ::start-create? true
                ::selected-municipality municipality-number
                ::selected-subdivision subdivision)
     :fx [[:dispatch [::municipality/load-municipalities true [::municipalities-loaded]]]]}))

(rf/reg-event-fx ::close-preliminary-selections
  ;; Minimal version of ::close-create for use with new application-wizard
  (fn [{:keys [db]} _]
    {:db (dissoc db ::start-create?)}))

(rf/reg-sub ::start-create?
  :-> ::start-create?)

(defn- dashboard-loading [tr]
  [:div.flex.justify-around>div.flex.gap-4
   (cc/loading-spinner {:with-overlay false :small true})
   [:span (tr [:loading]) "..."]])

(defn- select-permit-type-cards
  "Renders permit type selection using landing-cards."
  [tr selected-subdiv {:keys [country municipality] :as _selected-muni} project-view?]
  (let [permit-types        (<sub [(if project-view? ::permit-types-with-project ::permit-types)])
        descriptions        (<sub [::permit-type-descriptions])
        names               (<sub [::permit-type-names])
        lang                (<sub [:language])
        project             (<sub [::project])
        permit-type->groups (<sub [::permit-type->groups])
        not-mobile?         (<sub [::media-query/breakpoint-matches? :sm])
        application-wizard? (<sub [::ff/enabled? :feature-flag.id/application-wizard])
        description-lines   (if not-mobile? 3 1)
        card-data           (mapv (fn [pt]
                                    (let [custom-desc (get descriptions pt)
                                          custom-name (some-> (get names pt)
                                                              (i18n-model/get-text-from-entities-or-map lang))
                                          content     (or (some-> custom-desc
                                                                  :permit-type-config/permit-type-descriptions
                                                                  (i18n-model/get-text-from-entities-or-map lang))
                                                          (tr [(keyword :application-type.description pt)]))
                                          title       (or custom-name (tr [pt]))
                                          img         (ptc-model/permit-type->icon-path pt)
                                          href        (if application-wizard?
                                                        (href/branded-href (branding-model/or-default (-> permit-type->groups pt first :group/uri-path-id))
                                                                           :view/application-wizard
                                                                           {:step application-wizard/first-step-path}
                                                                           (p/assoc-if
                                                                             (util/mmap
                                                                               {:permit-type pt
                                                                                :group-id    (-> permit-type->groups pt first :db/id)}
                                                                               country municipality)
                                                                             :project project))
                                                        (href/branded-href (branding-model/or-default (-> permit-type->groups pt first :group/uri-path-id))
                                                                           :create-application-v2
                                                                           {:part 1}
                                                                           (cond-> {:subdivision  selected-subdiv
                                                                                    :municipality municipality
                                                                                    :permit-type  pt}
                                                                             project (merge {:project/id   project
                                                                                             :skip-project true}))))
                                          action-text (tr [:wizard/begin-create-application])
                                          description (rich-text/html-to-text content)]
                                      (util/mmap title img href action-text content description)))
                                  permit-types)]
    [:div.p-2.lg:pr-16 {:class "xl:max-w-[1280px]"}
     [cards/landing-cards {:cards             card-data
                           :description-lines description-lines}]]))

(defn select-permit-type [tr selected-subdiv {:keys [country municipality] :as selected-muni} project-view?]
  (if-not (<sub [::permit-types-loaded-for? [country municipality]])
    [dashboard-loading tr]
    [select-permit-type-cards tr selected-subdiv selected-muni project-view?]))

(defn subdiv-select [tr select-disabled? selected-subdiv all-subdivisions]
  [ui-select/select {:id           "subdivision-select"
                     :class-name   "full-width-select"
                     :allow-empty? false
                     :select-text  (tr [:municipalities/select-subdivision])
                     :label-text   (tr [:municipalities/subdivision])
                     :hide-label   true
                     :disabled     select-disabled?
                     :value        selected-subdiv
                     :on-change    #(>evt [::subdivision-selected %])
                     :val->str     (partial municipality-model/subdivision-name tr)
                     :values       all-subdivisions}])

(defn muni-select [tr select-disabled? selected-subdiv]
  (let [distinct-munis (<sub [::distinct-municipalities-in-subdivision])
        selected-id    (<sub [::selected-municipality])
        selected-muni  (p/find-first distinct-munis {:municipality selected-id})]
    [ui-select/select {:id           "municipality-select"
                       :class-name   "full-width-select"
                       :allow-empty? false
                       :select-text  (tr [:municipalities/select-municipality])
                       :label-text   (tr [:municipalities/municipality])
                       :hide-label   true
                       :disabled     (or select-disabled?
                                         (nil? selected-subdiv))
                       :value        selected-muni
                       :on-change    #(>evt [::municipality-selected selected-subdiv (:municipality %)])
                       :val->str     :municipality-name
                       :values       distinct-munis}]))

(defn create-selection-header [number content & [class]]
  [:div.font-bold.text-xl.mb-10.flex
   (when class {:class class})
   [:div.flex-initial
    [:div.rounded-full.border-2.border-solid.mr-2.h-14.w-14.p-4.text-center number]]
   [:div.flex-initial.mt-4.ml-4 content]])

(defn create-preliminary-selections [tr & [project-view?]]
  (when (<sub [::start-create?])
    (let [all-subdivisions (<sub [::subdivisions])
          selected-muni    (<sub [::selected-municipality-data])
          selected-subdiv  (<sub [::currently-active-subdivision])]
      [:section.dashlet
       [:div.md:mx-10
        [create-selection-header "1" (tr [:wizard/select-municipality])]
        (if-not (<sub [::municipality/municipalities-loaded?])
          [dashboard-loading tr]
          [:div.flex.md:ml-20
           [:div.flex-1.mr-2
            [subdiv-select tr (boolean project-view?) selected-subdiv all-subdivisions]]
           [:div.flex-1
            [muni-select tr (boolean project-view?) selected-subdiv]]])
        [:<>
         [create-selection-header "2" (tr [:wizard/select-type-of-application]) (str "mt-10 " (when (not selected-muni) "opacity-25"))]
         (when selected-muni
           [:div.md:ml-20
            [select-permit-type tr selected-subdiv selected-muni project-view?]])]]])))

(defn create-location [match tr]
  (let [{:keys [work-target work-types category permit-type] :as query-params} (get-in match [:parameters :query])
        search-pending?                                                        (<sub [::address-result-pending])
        search-term                                                            (<sub [::address-search-term])
        all-subdivisions                                                       (<sub [::subdivisions])
        selected-muni-id                                                       (<sub [::selected-municipality])
        selected-muni                                                          (<sub [::selected-municipality-data])
        selected-subdiv                                                        (<sub [::currently-active-subdivision])
        ag-municipalities                                                      (<sub [::authority-group-municipalities])
        lang                                                                   (<sub [:language])
        feature-data                                                           (location-from-query-params query-params)
        search-disabled?                                                       (nil? selected-muni)
        pending?                                                               (or (<sub [::pending?])
                                                                                   (and (not ag-municipalities)
                                                                                        (empty? all-subdivisions)))
        manual-input?                                                          (<sub [::manual-input?])
        gis-error?                                                             (<sub [::gis-error?])
        result-visible?                                                        (or (:x feature-data) manual-input? gis-error?)
        show-property-owners?                                                  (<sub [::show-property-owners-for-location?])
        location-not-required                                                  (<sub [::location-not-required category work-types work-target])
        missing-property-data?                                                 (str/blank? (:property/id feature-data))
        enhanced-pw?                                                           (and (ptc-model/permit-types-with-row-layers permit-type)
                                                                                    (ff/enabled? :feature-flag.id/create-workspace-with-manual-location))
        can-proceed?                                                           (if (and enhanced-pw? missing-property-data?)
                                                                                 (not (str/blank? (:location/description query-params)))
                                                                                 (and (<sub [::property-selected?])
                                                                                      (<sub [::valid-address?])))
        wizard-location-desc                                                   (if enhanced-pw?
                                                                                 (tr [:wizard/location-description-enhanced-pw]
                                                                                     [(property-model/format-property-id-for-sentence tr)])
                                                                                 (tr [:wizard/location-description]
                                                                                     [(property-model/format-property-id-for-sentence tr)]))]
    [:section#select-location
     [:div.bx--row.mb-4.items-center
      [:div.bx--col-xs-12.bx--col-lg-6.bx--col-xl-7.bx--col-xxl-8
       [:h2.mb-0 (tr [:wizard/select-location])]]]
     [:div.bx--row
      [:div.bx--col-xs-12
       [:p wizard-location-desc]]]
     (if pending?
       [:div.flex.justify-center.items-center
        (cc/loading-spinner {:with-overlay false})]
       [:div.bx--row {:class (when (and ag-municipalities (> (count ag-municipalities) 1))
                               "items-center")}
        (if (and ag-municipalities (> (count ag-municipalities) 1))
          ;; Authority-group flow: municipality select
          [:div.mt-3.bx--col-xs-12.bx--col-md-6.bx--col-xxl-4.py-3
           [ui-select/select
            {:id              "authority-group-municipality-select"
             :label-text      (tr [:wizard/select-municipality])
             :external-label? true
             :values          (mapv :municipality/number ag-municipalities)
             :val->str        (fn [number]
                                (some-> (p/find-first ag-municipalities {:municipality/number number})
                                        :municipality/name
                                        (i18n-model/get-text lang)))
             :value           selected-muni-id
             :allow-empty?    false
             :on-change       (fn [muni-number]
                                (when-let [muni (p/find-first ag-municipalities
                                                              {:municipality/number muni-number})]
                                  (>evt [::authority-group-municipality-selected muni])))}]]
          ;; Normal flow: display municipality name
          [:div.mt-3.bx--col-xs-12.bx--col-md-6.bx--col-xxl-4.py-3.text-lg
           (str (:municipality-name selected-muni)
                (when selected-subdiv
                  (str ", " (municipality-model/subdivision-name tr selected-subdiv))))])
        ;; Address search
        [gis-search/address-search
         {:class              [(if ag-municipalities
                                 "mt-3 bx--col-xs-12 bx--col-md-6 bx--col-xxl-8"
                                 "mt-3 bx--col-xs-12 bx--col-lg-6 bx--col-xxl-8")
                               (when search-disabled? "disabled")]
          :tr                 tr
          :domain-group-id    (<sub [::domain-group-id])
          :municipalities     [selected-muni-id]
          :disabled           search-disabled?
          :subdivision        selected-subdiv
          :on-select-address  #(>evt [::selected-address-event % show-property-owners?])
          :on-select-location #(>evt [::selected-location-event % show-property-owners?])
          :group-gis-layers?  true
          :layer-id-fn        ::layer-id-order
          :on-error-evt       [::set-gis-error true]}]])
     [:div.bx--row {:style {:min-height "32px"}}
      [:div.bx--col-xs-6
       (when search-pending?
         (cc/inline-loading {:description (tr [:wizard/searching])}))]
      [:div.bx--col-xs-6.flex.justify-end.items-center
       (when (and (not search-pending?)
                  (not result-visible?)
                  (string? search-term)
                  (> (count search-term) 5))
         [:div
          [link-button
           {:on-click #(>evt [::set-manual-input-enabled true])}
           (tr [:wizard/location-not-found?])]])]]
     ;; Map and results
     (when-not pending?
       [:div.bx--row.mt-2.mb-2
        (let [col-size      (if result-visible? 6 12)
              disabled-help (cond
                              ;; pre-selected municipality, but missing
                              (nil? selected-muni)
                              (tr [:wizard/location-selection-municipality-disabled])

                              ;; municipality not selected
                              search-disabled?
                              (tr [:wizard/location-selection-guide])

                              ;; all good
                              :else nil)]
          [map-wrapper-workspace-map col-size disabled-help show-property-owners? (when-not search-pending? (:geometry feature-data))])
        (when result-visible?
          [:div.bx--col-xs-12.bx--col-lg-6
           (cond
             (and enhanced-pw? missing-property-data? manual-input?)
             [manual-location-entry tr feature-data]

             manual-input?
             [manual-address-entry tr feature-data]

             missing-property-data?
             [missing-property-data tr]

             :else
             [selected-location tr feature-data enhanced-pw?])])])
     ;; Buttons
     [wizard-buttons
      tr
      {:next {:on-click #(>evt [::open-next-part match location-not-required])
              :disabled (not can-proceed?)}
       :back {:on-click #(>evt [::open-previous-part match location-not-required])}}]]))

(defn category-description [value category]
  ^{:key category}
  [ui-rt/editor-legacy {:initial-value value
                        :id            (str category "-description")}])

(defn select-category [{:keys [tr category-id type-and-target-disabled?
                               project-type work-type-ids work-target-id
                               pre-consultation-preference create-pre-consultation?
                               on-select-permit-category on-select-work-type on-select-work-target]}]
  (let [language              (<sub [:language])
        tr-quiet              (<sub [:tr/quiet])
        column-map            {:tr     tr
                               :height "300px"}
        all-categories        (<sub [::category-group-configs])
        selectable-categories (cond->> all-categories
                                project-type (filter (config-model/in-project-type-fn project-type)))
        selected-cat-config   (p/find-first selectable-categories {:db/id category-id})
        work-types            (when category-id
                                (<sub [::child-group-configs category-id]))
        work-targets          (when (seq work-type-ids)
                                (->> work-type-ids
                                     (mapcat #(<sub [::child-group-configs %]))
                                     doall))
        multiple-work-types?  (<sub [::multiple-work-types?])
        options-fn            (fn [configs selected?]
                                (->> configs
                                     (map #(let [title (i18n-model/get-text (:group.config/names %) language)]
                                             {:id        (:db/id %)
                                              :title     title
                                              :selected? (selected? (:db/id %))
                                              ;; TODO: Should not rely on target
                                              :data-cy   (if-let [k (:group.config/target %)]
                                                           (str (namespace k) "-" (name k))
                                                           (-> title normalize/keyword-from-name name))}))))]
    (when (not-empty selectable-categories)
      [:<>
       [:div
        [:h2 (tr [:wizard/select-type-and-target])]
        [:div.bg-ui-light-gray.pt-6.pl-6.pr-6.mb-6
         [:p.bold (tr [:wizard/description-label])]
         [:p.whitespace-pre-wrap (tr [:wizard/select-type-and-target-description])]]
        [:div.flex.gap-3.mb-4
         [:div.flex-auto
          [columns/column-select (merge column-map
                                        {:title     :wizard/category
                                         :options   (options-fn selectable-categories (partial = category-id))
                                         :icon-type :radio
                                         :on-change on-select-permit-category})]]
         (when (and (seq work-types)
                    (not type-and-target-disabled?))
           [:div.flex-auto
            [columns/column-select (merge column-map
                                          {:title     :work-type-select
                                           :options   (when-not type-and-target-disabled?
                                                        (options-fn work-types #(some (partial = %) work-type-ids)))
                                           :icon-type (if multiple-work-types? :checkbox :radio)
                                           :disabled? type-and-target-disabled?
                                           :on-change on-select-work-type})]])
         (when (and (seq work-targets)
                    (not type-and-target-disabled?))
           [:div.flex-auto
            [columns/column-select (merge column-map
                                          {:title     :work-target-select
                                           :options   (when-not type-and-target-disabled?
                                                        (options-fn work-targets (partial = work-target-id)))
                                           :icon-type :radio
                                           :disabled? type-and-target-disabled?
                                           :on-change on-select-work-target})]])]]
       (when selected-cat-config
         (let [standard-desc (and (nil? (:group.config/category-description selected-cat-config)) ; nil when not edited by admin
                                  (some->> (:group.config/target selected-cat-config)
                                           name
                                           (keyword "category.description")
                                           vector
                                           tr-quiet))
               modified-desc (when-let [desc (:group.config/category-description selected-cat-config)]
                               (when-not (ui-rt/empty-texts? desc)
                                 desc))]
           (when (or standard-desc modified-desc)
             [:div.my-10.bg-ui-light-gray.py-6.pl-6.pr-6.mb-6
              [:p.bold (tr [:wizard/description-label])]
              (if modified-desc
                [category-description modified-desc category-id]
                [:p.pb-0.whitespace-pre-wrap standard-desc])])))
       (when (#{:group.config.pre-consultation/optional
                :group.config.pre-consultation/required}
              pre-consultation-preference)
         (if (= pre-consultation-preference :group.config.pre-consultation/required)
           [:span (tr [:wizard/pre-consultation-mandatory-description])]
           [:<>
            [:span (tr [:wizard/pre-consultation-description])]
            [:div.w-48.mt-4
             (cc/content-switcher
               {:on-change      #(>evt [::set-create-pre-consultation (-> % (aget "name") (= "with-pre-consultation"))])
                :selected-index (if create-pre-consultation? 0 1)}
               (cc/switch {:text       (tr [:yes])
                           :index      0
                           :name       "with-pre-consultation"
                           :class-name "uppercase font-semibold"})
               (cc/switch {:text       (tr [:no])
                           :index      1
                           :name       "no-pre-consultation"
                           :class-name "uppercase font-semibold"}))]
            [:div.mt-6.text-gray-800.text-sm (tr [:wizard/pre-consultation-small-print])]]))])))

(def part-1-subsection
  :div.pt-6.mt-6.border-t.border-gray-400.border-solid)

(defn applicant-picker [tr]
  (let [options (<sub [::applicant-select-opts])
        value   (<sub [::selected-applicant])]
    [part-1-subsection
     [:h2.text-lg.mb-6 (tr [:wizard/select-applying-for])]
     [ui-select/select {:id           "business-applicant-picker"
                        :label-text   (tr [:wizard/initial-applicant])
                        :hide-label   true
                        :required     true
                        :allow-empty? false
                        :values       options
                        :val->str     :name
                        :value        value
                        :class-name   "unset-width-select"
                        :select-text  (tr [:wizard/select-applicant])
                        :on-change    #(>evt [::select-applicant %])}]]))

(defn submit-time-picker-impl [{:keys [tr submit-date on-change class width date-label time-label error]}]
  (let [submit-time (if submit-date
                      [(.getHours submit-date) (.getMinutes submit-date)]
                      [nil nil])]
    [:<>
     [:div.flex.flex-col.md:flex-row.items-end
      (util/mmap class)
      [ui/date-picker {:label-text (tr [(or date-label :wizard/submit-date)])
                       :required?  true
                       :class-name "md:mr-2"
                       :width      width
                       :max-date   (date-util/today-start-of-day)
                       :callback   #(when (some? %)
                                      (let [d (or submit-date (js/Date.))]
                                        (on-change (date-util/set-hours-and-minutes % (.getHours d) (.getMinutes d)))))
                       :value      submit-date}]
      [ui/time-input {:label-text (tr [(or time-label :wizard/submit-time)])
                      :required?  true
                      :class      "mt-2"
                      :on-change  (fn [[hour minute]]
                                    (on-change (date-util/set-hours-and-minutes
                                                 (or (date-util/start-of-day submit-date) (js/Date.))
                                                 hour
                                                 minute)))
                      :value      submit-time}]]
     (when (some-> submit-date (date-util/after? (date-util/now)))
       [:div.bx--form-requirement.flex.max-h-full (tr [(or error :error/submit-date-future)])])]))

(defn submit-time-picker [tr]
  (let [submit-date (<sub [::manual-submit-date])
        on-change   #(>evt [::set-submit-ts-query-param %])]
    [:div.flex.flex-col.mt-6.mb-4.animation-appear-slide-down
     [submit-time-picker-impl (util/mmap tr submit-date on-change)]]))

(defn create-mode-selector [tr]
  (let [value (<sub [::application-create-mode])]
    [part-1-subsection
     [:h2.text-lg.mb-6 (tr [:wizard/create-mode])]
     [ui-radio/radio {:name       "application-create-mode"
                      :class-name "flex-wrap"
                      :values     [:wizard.application-create-mode/default
                                   :wizard.application-create-mode/behalf-of-customer
                                   :wizard.application-create-mode/submitted-earlier]
                      :value      value
                      :val->str   (comp tr vector)
                      :on-change  #(>evt [::set-create-mode-query-param %])}]
     (when (= (<sub [::application-create-mode]) :wizard.application-create-mode/submitted-earlier)
       [submit-time-picker tr])]))

(defn create-category [match tr]
  [eb/error-boundary
   (r/with-let [on-select-permit-category #(>evt [::select-permit-category %])
                on-select-work-type       #(>evt [::select-work-type %])
                on-select-work-target     #(>evt [::select-work-target %])]
     (let [{{{:keys [work-target work-types category] :as query-params} :query} :parameters} match
           category-id                                                                       (:category query-params)
           work-type-ids                                                                     (:work-types query-params)
           work-target-id                                                                    (:work-target query-params)
           project-type                                                                      (:project-type query-params)
           tr-quiet                                                                          (<sub [:tr/quiet])
           permit-type                                                                       (<sub [::selected-permit-type])
           project-types                                                                     (<sub [::sorted-project-types])
           all-categories                                                                    (<sub [::category-group-configs])
           selectable-categories                                                             (cond->> all-categories
                                                                                               project-type (filter (config-model/in-project-type-fn project-type)))
           selected-cat-config                                                               (p/find-first selectable-categories {:db/id category-id})
           pre-consultation-preference                                                       (<sub [::pre-consultation-preference])
           create-pre-consultation?                                                          (<sub [::create-pre-consultation?])
           default-change-prevented?                                                         (:group.config/default-change-prevented? selected-cat-config)
           type-and-target-disabled?                                                         (or create-pre-consultation?
                                                                                                 default-change-prevented?)
           location-not-required                                                             (<sub [::location-not-required category work-types work-target])
           authority?                                                                        (<sub [::authority-user?])]
       (if permit-type
         [:section.bx--col-xs-auto.create-part-2.mt-8
          [:div
           (when (seq project-types)
             [:<>
              [:h2 (tr [:wizard/project-type])]
              [:div.bg-ui-light-gray.pt-6.pl-6.pr-6.mb-6
               [:p.bold (tr [:wizard/description-label])]
               [:p (tr [:wizard/project-type-description])]]
              [:div
               {:class "my-4 flex flex-wrap"}
               (for-frag [pt project-types
                          :when (seq (filter (config-model/in-project-type-fn pt) all-categories))]
                 (ui/radio-button-custom
                   "project-type"
                   {:value         pt
                    :curr-selected project-type
                    :class         "flex-1 p-0 m-2"
                    :content-class "w-full project-type-radio"
                    :radio-hidden? true
                    :on-change     #(>evt [::select-project-type (keyword :project-type (futil/tval %))])
                    :content       [:div
                                    (when (get keyword->image pt)
                                      [:img {:src   (str "/asset/img/" (get keyword->image pt))
                                             :class "my-8 mx-auto block"}])
                                    [:h4.my-4.text-center (tr-quiet [pt])]]}))]])]
          [select-category (util/mmap on-select-permit-category on-select-work-type on-select-work-target
                                      tr project-type category-id type-and-target-disabled? work-type-ids
                                      work-target-id pre-consultation-preference create-pre-consultation?)]
          (when authority?
            [create-mode-selector tr])
          (when (<sub [::show-my-businesses?])
            [applicant-picker tr])
          [wizard-buttons
           tr
           {:next {:on-click #(>evt [::open-next-part match location-not-required])
                   :text     (tr [:next])
                   :disabled (not (<sub [::permit-details-selected?]))}
            :back {:disabled true}}]]
         [:section
          [:div.my-4 (cc/skeleton-text {:heading true})]
          [:div.my-4 (cc/skeleton-text {:paragraph true})]])))])

(defn project-description [tr pending? projects]
  (if pending?
    [:div.bx--col-xs-12
     [cc/skeleton-text {:paragraph true :line-count 3}]]
    [:div.bx--col-xs-12
     [:p
      (if (seq projects)
        (tr [:wizard/project-description])
        (tr [:wizard/project-description.no-projects]))]]))

(defn my-projects [part query-params projects]
  (let [tr   (<sub [:tr])
        lang (<sub [:language])]
    (into
      (cc/tile-group
        {:name           "project-selection"
         :value-selected (or (:project/id query-params) "")
         :on-change      (fn [id]
                           (let [project                                   (p/find-first projects {:db/id id})
                                 {:municipality/keys [number subdivision]} (-> project :project/municipality)]
                             (>evt [::set-route-state
                                    part
                                    (p/assoc-if query-params
                                                :project/id id
                                                :municipality number
                                                :subdivision subdivision
                                                :single-mun (when number true))])))
         :class-name     "bx--row project-selection"})
      (for [{:keys [:db/id] :project/keys [name structural-address municipality property]} projects]
        (cc/radio-tile
          {:key        id
           :value      id
           :class-name "bx--col-xs-3 project-tile"}
          [:div.project-icon
           (cc/icon
             {:icon        i/document-list
              :description (tr [:select/project] [name])})]
          [:div.project-name
           [:div.font-bold.mb-2 name]
           [:div.leading-tight (str (or (:address/full-address structural-address)
                                        (:property/id property))
                                    ", "
                                    (municipality-model/common-name-province municipality tr lang))]])))))

(rf/reg-sub ::projects
  :<- [::entities/entities {:entity/type :entity.type/project}]
  :<- [::selected-municipality]
  (fn [[projects municipality] _]
    (->> projects
         (filter #(= municipality (-> % :project/municipality :municipality/number)))
         (filter (complement (du/in-state-fn :project.state/closed)))
         (filter #(-> % :granted-permissions :role.project/writer)))))

(rf/reg-event-db ::select-project-tab
  (fn [db [_ idx]]
    (assoc-in db [::create-application :project-tab] idx)))

(rf/reg-sub ::selected-project-tab
  :<- [::projects]
  :<- [::create-application-data]
  (fn [[projects {:keys [project-tab]}] _]
    (if (seq projects)
      (or project-tab 0)
      0)))

(defn create-project [match tr]
  (r/with-let [*project-name  (r/atom (get-in match [:parameters :query :project/name]))
               *name-invalid? (ra/reaction (< (count @*project-name) 3))]
    (let [{:keys [work-target work-types category] :as query-params} (get-in match [:parameters :query])
          my-num                                                     (get-in match [:parameters :path :part])
          pending?                                                   (<sub [::pending?])
          projects                                                   (<sub [::projects])
          project                                                    (:project/id query-params)
          invalid?                                                   (and (nil? project) @*name-invalid?)
          location-not-required                                      (<sub [::location-not-required category work-types work-target])
          next-page                                                  #(>evt [::open-next-part match location-not-required
                                                                             (-> query-params (assoc :project/name (if (nil? project) @*project-name "")))])]
      [:section#create-project-step
       [:div.bx--row>h2.bx--col-xs-12
        (tr [:wizard/project-heading])]
       [:div.bx--row
        [project-description tr pending? projects]]
       (if pending?
         (cc/tabs-skeleton)
         (let [selected-tab-idx (<sub [::selected-project-tab])
               selected-id      (if (and (seq projects) (= selected-tab-idx 0))
                                  :existing
                                  :create-new)]
           [ui-tabs/tabs
            {:tab-content-class "px-0 py-4"
             :selected-id       selected-id
             :on-change         (fn [id]
                                  (let [idx (if (= id :existing) 0 1)]
                                    (>evt [::select-project-tab idx])
                                    (case id
                                      :existing   (>evt [::set-route-state my-num (dissoc query-params :project/name)])
                                      :create-new (>evt [::set-route-state my-num (dissoc query-params
                                                                                          :project/id
                                                                                          :single-mun)]))))
             :tabs              (cond-> []
                                  (seq projects)
                                  (conj {:id      :existing
                                         :label   (tr [:project/select-existing])
                                         :content (if pending?
                                                    (cc/skeleton-text {})
                                                    [my-projects my-num query-params projects])})
                                  true
                                  (conj {:id      :create-new
                                         :label   (tr [:project/create-new])
                                         :data-cy "create-application-project-tab"
                                         :content [:div.w-full.lg:w-1-2
                                                   [ui/text-input
                                                    {:id         "project-name"
                                                     :auto-focus (empty? projects)
                                                     :on-key-up  #(when (and (futil/enter-press? %)
                                                                             (not invalid?))
                                                                    (next-page))
                                                     :value      (or @*project-name "")
                                                     :on-change  #(reset! *project-name (futil/tval %))
                                                     :label-text (tr [:project/name-label])}]]}))}]))
       [wizard-buttons tr {:next {:on-click next-page
                                  :disabled invalid?}
                           :back {:on-click #(>evt [::open-previous-part match location-not-required])}}]])))

(defn summary-item [title & content]
  [:<>
   [:div.bx--row>h3.bx--col-xs-12.uppercase.mb-4.mt-8
    title]
   (into [:div.bx--row>div.bx--col-xs-12]
         content)])

(defn summary-page [{{{:keys [permit-type category work-types work-target] :as query} :query} :parameters :as match}
                    tr]
  (let [can-proceed?             (<sub [::ready-to-create?])
        selected-authority-group (<sub [::selected-authority-group])
        create-pre-consultation? (<sub [::create-pre-consultation?])
        permit-type-config       (<sub [::selected-permit-type-group-config])
        permit-type-names        (<sub [::permit-type-names])
        category-config          (when category (<sub [::entities/entity category]))
        work-type-configs        (when-not (empty? work-types) (mapv #(<sub [::entities/entity %]) work-types))
        work-target-config       (when work-target (<sub [::entities/entity work-target]))
        actual-authority         (or (->> (concat [permit-type-config
                                                   category-config]
                                                  work-type-configs
                                                  [work-target-config])
                                          (some :group.config/approval-authority))
                                     selected-authority-group)
        lang                     (<sub [:language])
        permit-type-label        (or (some-> (get permit-type-names permit-type)
                                             (i18n-model/get-text-from-entities-or-map lang))
                                     (tr [permit-type]))
        location-not-required    (<sub [::location-not-required category work-types work-target])
        selected-applicant       (<sub [::selected-applicant])
        create-mode              (<sub [::application-create-mode])
        submitted-date           (<sub [::manual-submit-date])
        format-date              (<sub [::date-util/date-time-fn])
        property-owners          (<sub [::property-owners (property-selector/owner-cache-key query)])]
    [:section.summary-page
     [:div.bx--row>h2.bx--col-xs-12.mb-4
      (tr [:wizard/summary-page-heading])]
     (if can-proceed?
       [:<>
        [:div.bx--row>div.bx--col-xs-12
         [:span (tr [:wizard/summary-page-description] [(tr [:wizard/create-application])])]]
        (when-not (<sub [::location-not-required category work-types work-target])
          [summary-item
           (tr [:wizard/summary-location])
           ; location component here
           [location-tile tr (location-from-query-params query)]])
        [summary-item
         (tr [:wizard/summary-permit-type])
         [ui/text-rectangle permit-type-label]
         [ui/text-rectangle (config-model/localized-name category-config lang)]]
        (when-some [work-type-names (some->> work-type-configs
                                             seq
                                             (map #(config-model/localized-name % lang))
                                             sort)]
          [summary-item
           (tr [:wizard/work-type-and-target])
           (for-frag [work-type-name work-type-names]
             [ui/text-rectangle work-type-name])
           (when work-target
             [ui/text-rectangle (config-model/localized-name work-target-config lang)])])
        [summary-item
         (tr [:workspace/approval-authority])
         [ui/text-rectangle (:group/name actual-authority)]]
        (when selected-applicant
          [summary-item
           (tr [:role/applicant])
           [ui/text-rectangle
            (:name selected-applicant)]])
        (when (seq property-owners)
          [summary-item
           (tr [(if (next property-owners) :application.property/owners :application.property/owner)])
           [ui/text-rectangle (->> property-owners
                                   (map :name)
                                   (str/join ", "))]])
        (when (not= create-mode :wizard.application-create-mode/default)
          [summary-item
           (tr [:wizard/create-mode])
           [ui/text-rectangle
            (str (tr [create-mode])
                 (when submitted-date
                   (str ": " (format-date submitted-date))))]])]
       [:div.bx--row>p.bx--col-xs-12
        (tr [:wizard/missing-information])])
     (if (<sub [::config-loaded?])
       [wizard-buttons tr {:next {:on-click #(>evt [::create-application
                                                    create-pre-consultation?])
                                  :text     (tr [:wizard/create-application])
                                  :disabled (not (<sub [::can-finish-creation?]))}
                           :back {:on-click #(>evt [::open-previous-part match location-not-required])}}]
       [wizard-button-container
        (cc/loading-spinner {:with-overlay false})])]))


(defn create-app-init [{{{:keys [part]} :path} :parameters :as match}]
  (let [tr              (<sub [::tr])
        progress-vector (<sub [::allowed-parts])]
    [:div.cloudpermit-page.create-application
     [ui/page-header (util/mmap match)
      [:div.page-heading.bx--row
       [:h1.bx--col-xs-12 (tr [:application/create])]]
      [:div.bx--row
       [:div.bx--col-xs-12
        (into
          (cc/progress-indicator
            {:current-index (.indexOf progress-vector part)})
          (for [part progress-vector]
            (cc/progress-step
              {:label (tr [(keyword "wizard-v2.part" (str part))])})))]]]
     (case part
       1 [create-category match tr]
       2 [create-project match tr]
       3 [create-location match tr]
       4 [summary-page match tr]
       [not-found/not-found-view])]))

(def view-config
  {:name        :create-application-v2
   :view        create-app-init
   :controllers [{:start (fn [_]
                           (>evt [::initialize-view true])
                           (>evt [::municipality/load-municipalities true [::municipalities-loaded]]))}
                 {:parameters {:query [:subdivision :municipality :project/name :authority-group-id :permit-type]}
                  :start      (fn [{{:keys        [subdivision municipality authority-group-id permit-type]
                                     project-name :project/name}                                            :query}]
                                (when project-name
                                  (>evt [::select-project-tab 1]))
                                (if authority-group-id
                                  (>evt [::set-authority-group-id authority-group-id permit-type])
                                  (>evt [::set-subdiv-and-municipality subdivision municipality])))}
                 ;; called when path changes
                 {:parameters {:path [:part]}
                  :start      (fn [{:keys [path]}]
                                (case (get path :part)
                                  2 (>evt [::load-user-projects])
                                  1 (>evt [::init-work-type-n-target])
                                  nil))}
                 {:parameters {:path [:part] :query (conj property-selector/location-keys-without-project :zoom)}
                  :start      (fn [{:keys [path query]}]
                                (case (:part path)
                                  3 (>evt [::setup-location-from-parameters query])
                                  nil))}
                 {:parameters {:query [:municipality :permit-type :project-type :category :work-types :work-target]}
                  :start      (fn [{{:keys [permit-type]} :query}]
                                (when permit-type
                                  (>evt [::init-work-type-n-target])
                                  (when (business-model/business-parties-enabled?
                                          permit-type
                                          (ff/enabled? :feature-flag.id/business-parties-all-permit-types))
                                    (>evt [::fetch-my-businesses]))))}
                 {:parameters {:query [:applicant-type :applicant-id]}
                  :start      (fn [{{:keys [applicant-type applicant-id]} :query}]
                                (>evt [::set-selected-applicant-id applicant-type applicant-id]))}
                 {:parameters {:query [:create-mode :submit-ts]}
                  :start      (fn [{{:keys [create-mode submit-ts]} :query}]
                                (>evt [::set-application-create-mode create-mode])
                                (>evt [::set-manual-submit-date create-mode submit-ts]))}]
   :public?     false
   :parameters  {:path  {:part s/Int}
                 :query (st/optional-keys WizardParams)}})
